import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import { companyApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  Icon,
  Input,
  LinkButton,
  LoadingState,
  PageHeader,
  statusLabel,
  Textarea,
  useToast,
} from '@/components/ui';
import { applyFieldErrors, errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';
import { formatDate } from '@/utils/format';
import { companyVerificationSchema } from '@/validations';

const STATUS_COPY = {
  UNVERIFIED: {
    tone: 'neutral',
    title: 'Not verified yet',
    body: 'Verified companies rank higher in search and show a badge on every listing.',
  },
  PENDING: {
    tone: 'warning',
    title: 'Under review',
    body: 'Our team is reviewing your request. This usually takes one or two working days.',
  },
  VERIFIED: {
    tone: 'success',
    title: 'Verified',
    body: 'Your company is verified. The badge appears on your profile and internships.',
  },
  REJECTED: {
    tone: 'danger',
    title: 'Not approved',
    body: 'Your last request was not approved. Update your details and try again.',
  },
};

const BENEFITS = [
  'A verified badge on your profile and every internship',
  'Higher placement in student search results',
  'Access to featured placement on the landing page',
];

/** The status card takes a wash of its tone, so the state reads at a glance. */
const STATUS_CARD = {
  neutral: '',
  warning: 'border-warning-200 bg-warning-50/40',
  success: 'border-success-200 bg-success-50/40',
  danger: 'border-danger-200 bg-danger-50/40',
};

const STATUS_ICON_TONES = {
  neutral: 'bg-surface-muted text-ink-muted ring-line',
  warning: 'bg-warning-50 text-warning-800 ring-warning-100',
  success: 'bg-success-50 text-success-700 ring-success-100',
  danger: 'bg-danger-50 text-danger-600 ring-danger-100',
};

export function CompanyVerificationPage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['company-verification'],
    queryFn: companyApi.getVerification,
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(companyVerificationSchema),
    // Filled in from the last request once the status has loaded.
    values: data && {
      registration_number: data.registration_number ?? '',
      note: data.note ?? '',
    },
  });

  const submit = useMutation({
    mutationFn: (values) => companyApi.submitVerification(values),
    onSuccess: () => {
      toast.success('Verification request submitted');
      void queryClient.invalidateQueries({ queryKey: ['company-verification'] });
      void queryClient.invalidateQueries({ queryKey: ['company-profile'] });
    },
  });

  if (isPending) return <LoadingState label="Loading verification status…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const status = STATUS_COPY[data.verification_status] ?? STATUS_COPY.UNVERIFIED;

  async function onSubmit(values) {
    const payload = Object.fromEntries(
      Object.entries(values).filter(([, value]) => value !== '' && value !== undefined),
    );
    try {
      await submit.mutateAsync(payload);
    } catch (mutationError) {
      if (!applyFieldErrors(mutationError, setError)) {
        toast.error(errorMessage(mutationError, 'Could not submit the request'));
      }
    }
  }

  const isVerified = data.verification_status === 'VERIFIED';
  const hasRequested = data.verification_status === 'PENDING' || isVerified;
  const steps = [
    { key: 'profile', label: 'Complete your profile', done: data.blocking_sections.length === 0 },
    { key: 'submit', label: 'Submit for review', done: hasRequested },
    { key: 'verified', label: 'Verified', done: isVerified },
  ];

  return (
    // On a phone or tablet held upright -- iPad Pro included -- the cards stack
    // in one column and the last one runs to the bottom of the screen. Side by
    // side only on a wide screen held sideways.
    <div className="flex flex-col portrait:min-h-[calc(100dvh-6rem)]">
      <PageHeader
        title="Verification"
        description="Prove your company is real so students can apply with confidence."
        action={
          <Badge dot tone={status.tone}>
            {statusLabel(data.verification_status)}
          </Badge>
        }
      />

      <div className="flex flex-1 flex-col gap-6 lg:landscape:grid lg:landscape:grid-cols-[1fr_340px] lg:landscape:items-start">
        <div className="flex flex-col gap-6">
          <Card className={STATUS_CARD[status.tone]}>
            <div className="flex items-start gap-4">
              <span
                aria-hidden="true"
                className={cn(
                  'grid size-11 shrink-0 place-items-center rounded-xl ring-1 ring-inset',
                  STATUS_ICON_TONES[status.tone],
                )}
              >
                <Icon name="verification" size={20} />
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-ink text-base font-semibold tracking-tight">
                    {status.title}
                  </h2>
                  <Badge dot tone={status.tone}>
                    {statusLabel(data.verification_status)}
                  </Badge>
                </div>
                <p className="text-ink-muted mt-1 text-sm">{status.body}</p>
                {/* The admin's own words, kept on the company rather than only
                    sent by email. Gone once a new request is submitted. */}
                {data.verification_status === 'REJECTED' && data.rejection_reason && (
                  <div className="border-danger-200 bg-surface mt-3 rounded-lg border px-3 py-2.5">
                    <p className="text-danger-700 text-xs font-semibold">
                      Reason from Team Join Wonder
                      {data.rejected_at && (
                        <span className="text-ink-muted font-normal">
                          {' '}
                          · {formatDate(data.rejected_at)}
                        </span>
                      )}
                    </p>
                    <p className="text-ink mt-1 text-sm break-words whitespace-pre-line">
                      {data.rejection_reason}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <ol className="border-line mt-5 grid gap-3 border-t pt-5 sm:grid-cols-3">
              {steps.map((step, index) => (
                <li key={step.key} className="flex items-center gap-2.5 text-sm">
                  {step.done ? (
                    <span
                      aria-hidden="true"
                      className="bg-success-600 grid size-6 shrink-0 place-items-center rounded-full text-white"
                    >
                      <Icon name="check" size={14} />
                    </span>
                  ) : (
                    <span
                      aria-hidden="true"
                      className="border-line text-ink-muted grid size-6 shrink-0 place-items-center rounded-full border-2 text-xs font-semibold"
                    >
                      {index + 1}
                    </span>
                  )}
                  <span className={step.done ? 'text-ink font-medium' : 'text-ink-muted'}>
                    {step.label}
                    <span className="sr-only">{step.done ? ' (done)' : ' (to do)'}</span>
                  </span>
                </li>
              ))}
            </ol>
          </Card>

          {(data.verification_status === 'UNVERIFIED' ||
            data.verification_status === 'REJECTED') && (
            <Alert tone="info" title="Need help getting verified?">
              Our guide shows you what to fill in, one step at a time. At the end, it sends your
              request to our team.
              <div className="mt-3">
                <LinkButton to={paths.company.verificationGuide} size="sm">
                  Start verification
                  <Icon name="arrowRight" size={16} />
                </LinkButton>
              </div>
            </Alert>
          )}

          {data.can_submit && (
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6" noValidate>
              <Card>
                <CardHeader
                  title="Request verification"
                  description="We check your company registration against public records."
                />
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Input
                      label="Company registration number"
                      hint="CIN, GSTIN or an equivalent registration. It speeds up the review."
                      error={errors.registration_number?.message}
                      {...register('registration_number')}
                    />
                  </div>
                  <Textarea
                    label="Anything we should know?"
                    rows={4}
                    error={errors.note?.message}
                    {...register('note')}
                  />
                </div>
              </Card>
              <div className="flex justify-end gap-2">
                <Button type="submit" isLoading={isSubmitting || submit.isPending}>
                  Submit for review
                </Button>
              </div>
            </form>
          )}
        </div>

        <Card className="portrait:flex-1">
          <CardHeader title="What verification gives you" />
          <ul className="space-y-3 text-sm">
            {BENEFITS.map((benefit) => (
              <li key={benefit} className="flex items-start gap-2.5">
                <span
                  aria-hidden="true"
                  className="bg-success-50 text-success-700 mt-px grid size-5 shrink-0 place-items-center rounded-full"
                >
                  <Icon name="check" size={12} />
                </span>
                <span className="text-ink-muted">{benefit}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
